import { Link, useSearchParams } from 'react-router-dom';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { isNavItemVisible, NAV_ITEMS } from '../layout/navigation.ts';
import {
  buttonClass,
  EmptyState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/index.ts';
import { ApiReference } from './admin-api/ApiReference.tsx';
import { ManagementKeys } from './admin-api/ManagementKeys.tsx';

/**
 * `/admin-api` — org management keys and the public admin API reference.
 *
 * Visibility matches the nav row: owner and admin only. A member or
 * finance session that types the URL gets the same in-page forbidden
 * state `/billing` uses, and the keys query stays unmounted so it never
 * fires.
 */

const ADMIN_API_NAV = NAV_ITEMS.find((item) => item.to === '/admin-api');

const TAB_IDS = ['keys', 'reference'] as const;
type TabId = (typeof TAB_IDS)[number];
const DEFAULT_TAB: TabId = 'keys';

function isTabId(value: string | null): value is TabId {
  return value === 'keys' || value === 'reference';
}

export function AdminApi(): JSX.Element {
  const { user } = useSession();
  if (ADMIN_API_NAV === undefined || !isNavItemVisible(user, ADMIN_API_NAV)) {
    return <AdminApiForbidden />;
  }
  return <AdminApiContent />;
}

function AdminApiForbidden(): JSX.Element {
  const t = useT();
  return (
    <section data-testid="admin-api-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('adminApi.title')}
        sub={t('adminApi.subtitle')}
        testId="admin-api-header"
      />
      <div data-testid="admin-api-forbidden" className="rounded-lg border border-line-soft bg-card">
        <EmptyState
          title={t('adminApi.forbidden.title')}
          hint={t('adminApi.forbidden.body')}
          action={
            <Link
              to="/dashboard"
              className={buttonClass('secondary')}
              data-testid="admin-api-forbidden-cta"
            >
              {t('adminApi.forbidden.cta')}
            </Link>
          }
        />
      </div>
    </section>
  );
}

function AdminApiContent(): JSX.Element {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const activeTab: TabId = isTabId(requested) ? requested : DEFAULT_TAB;

  function selectTab(tab: string): void {
    if (!isTabId(tab)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === DEFAULT_TAB) {
          next.delete('tab');
        } else {
          next.set('tab', tab);
        }
        return next;
      },
      { replace: false },
    );
  }

  return (
    <section data-testid="admin-api-page" className="flex flex-col gap-5">
      <PageHeader
        title={t('adminApi.title')}
        sub={t('adminApi.subtitle')}
        testId="admin-api-header"
      />
      <Tabs value={activeTab} onValueChange={selectTab}>
        <TabsList ariaLabel={t('adminApi.tabs.aria')}>
          <TabsTrigger value="keys" testId="admin-api-tab-keys">
            {t('adminApi.tab.keys')}
          </TabsTrigger>
          <TabsTrigger value="reference" testId="admin-api-tab-reference">
            {t('adminApi.tab.reference')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="keys" className="pt-5">
          <ManagementKeys />
        </TabsContent>
        <TabsContent value="reference" className="pt-5">
          <ApiReference />
        </TabsContent>
      </Tabs>
    </section>
  );
}
